














































import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { botsApi } from '../../services/bots';
import ConversationControlBar from './ConversationControlBar.vue';
import BrowserUrlBar from './BrowserUrlBar.vue';
import WebAssistantPreview from '@/features/webassistant/components/WebAssistantPreview.vue';
import PhonePreview from './PhonePreview.vue';
import BotPausedBanner from './BotPausedBanner.vue';
import { ConversationMessage } from '@/features/webassistant/models/conversation-message';
import { MessageStatus } from '@/features/webassistant/models/messageStatus';
import { MessageDirection } from '@/features/webassistant/models/messageDirection';
import { FlowListItemViewModel } from '@/features/bots/models/flowListItemViewModel';
import shortid from 'shortid';
import { AppCard } from '@/features/apps/shared/models/appCard';
import { ExecuteTestBotViewModel, FlowAndPriority } from '@/features/bots/models/request/executeTestBotViewModel';
import { BotUserViewModel } from '@/features/bots/models/botUserViewModel';
import { BotTestResponseViewModel } from '@/features/bots/models/botTestResponseViewModel';
import { InstructionViewModel, SendAppInstructionViewModel } from '@/features/bots/models/instructionViewModel';
import ContactNameUtil from '@/features/bots/contactNameUtil';
import { BotAppInitViewModel } from '@/features/bots/models/request/botAppInitViewModel';
import { BotAppSubmitViewModel } from '@/features/bots/models/request/botAppSubmitViewModel';
import appInsights from '@/shared/services/appInsights';
import { AnswerListItemViewModel } from '@/features/bots/models/answers/answerListItemViewModel';

@Component({
	components: {
		PhonePreview,
		ConversationControlBar,
		BrowserUrlBar,
		WebAssistantPreview,
		BotPausedBanner
	}
})
export default class BotPreviewMessagingClientContainer extends Vue {
	@Getter public currentWorkspace!: WorkspaceViewModel;
	@Prop() public currentTime: Date;
	@Prop() public user: BotUserViewModel;
	@Prop() public flows!: FlowListItemViewModel[];
	@Prop() public answers!: AnswerListItemViewModel[];

	public messages: ConversationMessage[] = [];
	public sessionId: string;
	public variables: { [key: string]: any } = {};

	public isConversationVisible = true;
	public isBotPaused = false;
	public currentUser: BotUserViewModel = { id: '', data: {} };
	public currentUrl: string = 'http://home';
	public currentChannelType: string = 'web-assistant';
	public currentChannelIdentityId: string = 'web-assistant-default';

	public postbackTimers: number[] = [];

	@Watch('user', { immediate: true, deep: true })
	public onBotUserChanged(value: BotUserViewModel) {
		if (this.user) {
			this.currentUser = value;
		}
		this.$emit('userUpdated', value);
	}

	public async mounted() {
		this.sessionId = shortid.generate();
		this.currentUser.data['appUser.currentUrl'] = this.currentUrl;
		await this.sendPostback('widget:load');
		await this.sendPostback('conversation:open');
		await this.setupTimeOnPageTimers();
		document.addEventListener('fresco-panel-submit', this.cardSubmitHandler);
	}

	public beforeDestroy() {
		document.removeEventListener('fresco-panel-submit', this.cardSubmitHandler);
		this.deletePostbackTimers();
		this.deleteSession();
	}

	public async handleMessageSend(message: string) {
		const sentMessage = this.addOutboundMessage(message);
		if (this.isBotPaused) {
			return;
		}
		try {
			const request: ExecuteTestBotViewModel = {
				sessionId: this.sessionId,
				event: {
					type: 'message:appUser',
					source: {
						id: this.currentChannelIdentityId,
						type: this.currentChannelType
					},
					data: {
						'message.text': message,
						'system.currentTimeUtc': this.currentTime.toISOString()
					}
				},
				user: this.currentUser,
				flows: this.flowsAndPriorities,
				variables: this.variables
			};
			const response = await botsApi.sendTestBotEvent(this.currentWorkspace.id, request);
			sentMessage.status = MessageStatus.DeliverySucceeded;
			await this.handleBotResponse(response, true);
		} catch (e) {
			await this.handleError();
		} finally {
			this.trackTestBotEventSent('message:appUser');
		}
	}

	public async sendConversationHandover() {
		try {
			const request: ExecuteTestBotViewModel = {
				sessionId: this.sessionId,
				event: {
					type: 'conversation:handover',
					source: {
						id: this.currentChannelIdentityId,
						type: this.currentChannelType
					},
					data: {
						'system.currentTimeUtc': this.currentTime.toISOString()
					}
				},
				user: this.currentUser,
				flows: this.flowsAndPriorities,
				variables: this.variables
			};
			this.deletePostbackTimers();
			const response = await botsApi.sendTestBotEvent(this.currentWorkspace.id, request);
			await this.handleBotResponse(response);
		} catch (e) {
			await this.handleError();
		} finally {
			this.trackTestBotEventSent('conversation:handover');
		}
	}

	public async sendPostback(postbackType: string) {
		if (this.isBotPaused) {
			return;
		}
		try {
			const request: ExecuteTestBotViewModel = {
				sessionId: this.sessionId,
				event: {
					type: 'postback',
					source: {
						id: this.currentChannelIdentityId,
						type: this.currentChannelType
					},
					data: {
						'postback.payload': postbackType,
						'system.currentTimeUtc': this.currentTime.toISOString()
					}
				},
				user: this.currentUser,
				flows: this.flowsAndPriorities,
				variables: this.variables
			};
			const response = await botsApi.sendTestBotEvent(this.currentWorkspace.id, request);
			await this.handleBotResponse(response);
		} catch (e) {
			await this.handleError();
		} finally {
			this.trackTestBotEventSent('postback');
		}
	}

	public toggleConversationVisibility() {
		this.isConversationVisible = !this.isConversationVisible;
		if (this.isConversationVisible) {
			this.sendPostback('conversation:open');
		}
	}

	public handleQuickReply(quickReply: any) {
		this.handleMessageSend(quickReply.text);
	}

	public handleLoad($state: any) {
		$state.loaded();
		$state.complete();
	}

	public async urlChanged(url: string) {
		this.currentUrl = url;
		this.currentUser.data['appUser.currentUrl'] = url;
		appInsights.trackEvent({ name: 'Bot Preview: Url changed' }, { workspaceId: this.currentWorkspace.id, url });
		await this.resetPostbacks();
	}

	public async channelChanged(type: string, id: string) {
		this.$emit('channelChanged');
		const channelChanged = this.currentChannelType !== type || this.currentChannelIdentityId !== id;
		if (channelChanged) {
			appInsights.trackEvent({ name: 'Bot Preview: Channel changed' }, { workspaceId: this.currentWorkspace.id, channelId: id, channelType: type });
			this.currentChannelType = type;
			this.currentChannelIdentityId = id;
			this.reset();
			if (this.currentChannelType === 'web-assistant') {
				await this.sendPostback('widget:load');
				if (this.isConversationVisible) {
					await this.sendPostback('conversation:open');
				}
				await this.setupTimeOnPageTimers();
			}
		}
	}

	public async refreshClicked() {
		appInsights.trackEvent({ name: 'Bot Preview: Refresh clicked' }, { workspaceId: this.currentWorkspace.id });
		await this.resetPostbacks();
	}

	public resetClicked() {
		this.resetConversation();
	}

	public resumeBot() {
		this.isBotPaused = false;
	}

	public get isWebAssistant() {
		return this.currentChannelType === 'web-assistant';
	}

	private get flowsAndPriorities(): FlowAndPriority[] {
		return [
			...this.flows.map(f => ({ id: f.id, priority: f.priority })),
			...this.answers.map(f => ({ id: f.id, priority: 1 }))
		];
	}

	private addOutboundMessage(message: string): ConversationMessage {
		const sentMessage: ConversationMessage = {
			id: shortid.generate(),
			type: 'text',
			text: message,
			direction: MessageDirection.Outbound,
			timestampUtc: this.currentTime,
			status: MessageStatus.Sent
		};
		this.messages.push(sentMessage);
		this.scrollToBottom();
		return sentMessage;
	}

	private async resetPostbacks() {
		this.deletePostbackTimers();
		await this.sendPostback('widget:load');
		await this.setupTimeOnPageTimers();
	}

	private async handleBotResponse(response: BotTestResponseViewModel, isFromAppUserMessageRequest = false) {
		this.currentUser = this.getCurrentUser(response);
		if (isFromAppUserMessageRequest) {
			this.currentUser.data['appUser.lastHeardFrom'] = new Date();
		}

		this.variables = response.variables;
		this.$emit('userUpdated', this.currentUser);
		await this.handleBotInstructions(response);
		this.scrollToBottom();
	}

	private getCurrentUser(response: BotTestResponseViewModel) {
		const newUser = response.user;
		if (!newUser) {
			return newUser;
		}

		newUser.data['appUser.firstSeen'] = this.currentUser.data['appUser.firstSeen'] || new Date();
		newUser.data['appUser.lastSeen'] = new Date();

		for (const [key, value] of Object.entries(newUser.data)) {
			if (key === 'appUser.name') {
				newUser.data['appUser.givenName'] = ContactNameUtil.extractGivenName(value);
				newUser.data['appUser.surname'] = ContactNameUtil.extractSurname(value);
			}
		}

		if (this.containsMessageInstructions(response)) {
			newUser.data['appUser.lastContacted'] = new Date();
		}

		return newUser;
	}

	private containsMessageInstructions(response: BotTestResponseViewModel) {
		return response.instructions &&
				response.instructions.length > 0 &&
				response.instructions.filter(instruction => !instruction.whisper).length > 0;
	}

	private async handleBotInstructions(botResponse: BotTestResponseViewModel) {
		for (const instruction of botResponse.instructions) {
			if (instruction.sendApp) {
				await this.handleAppInstruction(instruction.sendApp, botResponse.variables, botResponse.user);
			} else if (instruction.handover) {
				this.isBotPaused = true;
				await this.sendConversationHandover();
			} else {
				this.messages.push(
					this.createTextConversationMessage(
						this.getMessageType(instruction),
						this.getMessageText(instruction),
						instruction.ask ? instruction.ask.options : null
					)
				);
				this.scrollToBottom();
			}
		}
	}

	private async handleAppInstruction(
		appInstruction: SendAppInstructionViewModel,
		variables: { [p: string]: any },
		user: BotUserViewModel
	) {
		const context = { location: 'bot-preview', channel: this.currentChannelType };
		const botAppInitViewModel = {
			user: this.getAppUserFromUser(user),
			config: appInstruction.config,
			context,
			data: variables
		} as BotAppInitViewModel;

		const appResponse = await botsApi.botAppInit(this.currentWorkspace.id, appInstruction.installedAppId, botAppInitViewModel);
		if (appResponse.card) {
			const conversationMessage = this.createAppConversationMessage(appResponse.card, appInstruction.installedAppId);
			this.messages.push(conversationMessage);
			this.scrollToBottom();
		} else if (appResponse.flow) {
			appResponse.flow.messages.forEach(appMsg => this.messages.push(this.createTextConversationMessage('text', appMsg.text)));
			this.scrollToBottom();
		}
		await this.initiateNextBotTurn();
	}

	private async initiateNextBotTurn() {
		try {
			const request: ExecuteTestBotViewModel = {
				sessionId: this.sessionId,
				event: {
					type: 'app:response',
					source: {
						id: this.currentChannelIdentityId,
						type: this.currentChannelType
					},
					data: {
						'system.currentTimeUtc': this.currentTime.toISOString()
					}
				},
				user: this.currentUser,
				flows: this.flowsAndPriorities,
				variables: this.variables
			};
			const response = await botsApi.sendTestBotEvent(this.currentWorkspace.id, request);
			await this.handleBotResponse(response);
		} catch (e) {
			await this.handleError();
		} finally {
			this.trackTestBotEventSent('app:response');
		}
	}

	private async handleError() {
		await this.deleteSession();
		this.messages.push(this.createTextConversationMessage('text', 'Something went wrong, please try again'));
		this.scrollToBottom();
	}

	private async deleteSession() {
		await botsApi.deleteSession(this.currentWorkspace.id, this.sessionId);
	}

	private createTextConversationMessage(type: string, text: string, actions: string[] | null = null): ConversationMessage {
		appInsights.trackEvent({ name: 'Bot Preview: Message received' }, { workspaceId: this.currentWorkspace.id, type });
		return {
			id: shortid.generate(),
			type,
			text,
			direction: MessageDirection.Inbound,
			status: MessageStatus.DeliverySucceeded,
			timestampUtc: this.currentTime,
			actions
		};
	}

	private createAppConversationMessage(card: AppCard, installedAppId: string): ConversationMessage {
		const id = shortid.generate();
		return {
			id,
			type: 'app',
			direction: MessageDirection.Inbound,
			status: MessageStatus.DeliverySucceeded,
			timestampUtc: this.currentTime,
			card: { id, content: card },
			app: {
				installedAppId
			}
		} as ConversationMessage;
	}

	private getMessageType(instruction: InstructionViewModel): string {
		if (instruction.say || instruction.ask) {
			return 'text';
		} else if (instruction.whisper) {
			return 'whisper';
		} else if (instruction.sendApp) {
			return this.isWebAssistant ? 'app' : 'text';
		}
		return '';
	}

	private getMessageText(instruction: InstructionViewModel): string {
		if (instruction.say) {
			return instruction.say.text;
		} else if (instruction.ask) {
			return instruction.ask.text;
		} else if (instruction.whisper) {
			return instruction.whisper.text;
		}
		return '';
	}

	private reset() {
		this.currentUser = { id: '', data: {} };
		this.currentUser.data['appUser.currentUrl'] = this.currentUrl;

		this.resetConversation();
	}

	private resetConversation() {
		this.deleteSession();

		this.messages = [];
		this.sessionId = shortid.generate();
		this.variables = {};
		this.isBotPaused = false;

		this.deletePostbackTimers();
	}

	private scrollToBottom() {
		this.$nextTick(() => {
			const component = this.$refs.previewComponent as Vue;
			if (component) {
				const container = component.$el.getElementsByClassName('n-conversation-thread')[0];
				container.scrollTop = container.scrollHeight;
			}
		});
	}

	private async setupTimeOnPageTimers() {
		for (const flow of this.flows) {
			if (flow && flow.timeOnPage > 0) {
				const postbackTimer = window.setTimeout(() => this.sendPostback(`widget:load+${flow.timeOnPage}`), flow.timeOnPage * 1000);
				this.postbackTimers.push(postbackTimer);
			}
		}
	}

	private deletePostbackTimers() {
		for (let i = this.postbackTimers.length - 1; i >= 0; i--) {
			window.clearTimeout(this.postbackTimers[i]);
			this.postbackTimers.splice(i, 1);
		}
	}

	private async cardSubmitHandler(evt: any) {
		const eventDetail = evt.detail;
		const message = this.messages.find(m => m.id === eventDetail.panelId);
		if (message && message.app && message.app.installedAppId) {
			await this.submit(message, eventDetail);
		}
	}

	private async submit(message: ConversationMessage, eventDetail: any) {
		const submitData = await botsApi.botAppSubmit(this.currentWorkspace.id, message.app!.installedAppId, {
			user: this.getAppUserFromUser(this.currentUser),
			componentId: eventDetail.componentId,
			storedData: this.containsStoredData(message) ? message!.card!.content!.storedData : {},
			data: this.variables,
			inputData: this.getInputData(eventDetail.parsedValues),
			context: { location: 'bot-preview', channel: this.currentChannelType }
		} as BotAppSubmitViewModel);

		if (message.card && message.card.content) {
			message.card.content = submitData.card;
		}
	}

	private containsStoredData(message: ConversationMessage) {
		return message.card && message.card.content && message.card.content.storedData;
	}

	private getInputData(frescoValues?: any[]): { [key: string]: string } {
		const values: { [key: string]: string } = {};
		if (frescoValues) {
			for (const value of frescoValues) {
				values[value.id] = value.value;
			}
		}
		return values;
	}

	private getAppUserFromUser(user: BotUserViewModel) {
		if (!user) {
			return {};
		}
		return {
			id: user.id,
			givenName: user.data['appUser.givenName'],
			surname: user.data['appUser.surname'],
			email: user.data['appUser.email'],
			phoneNumber: user.data['appUser.phoneNumber'],
			createdTimeUtc: user.data['appUser.firstSeen'],
			customAttributes: this.getCustomAttributesFromUser(user)
		};
	}

	private getCustomAttributesFromUser(user: BotUserViewModel) {
		return Object.entries(user.data)
			.filter(([key, value]) => key.startsWith('appUser.customAttributes.'))
			.reduce(
				(prev, [key, value]) => {
					prev[key.replace('appUser.customAttributes.', '') as string] = value;
					return prev;
				},
				{} as { [key: string]: any }
			);
	}


	private trackTestBotEventSent(eventType: string) {
		appInsights.trackEvent({ name: 'Bot Preview: Test bot event sent' }, { workspaceId: this.currentWorkspace.id, sessionId: this.sessionId, eventType });
	}

}
